import { PlusCircleIcon } from '@heroicons/react/24/outline';
import {
  SectionSlot,
  SpecialEventEnum,
  SpecialEventWithoutDates,
  UserPreference,
  UserPreferenceType,
} from '@youshift/shared/types';
import {
  ColorName,
  dateToString,
  getShadeForLabels,
  localeNormalizer,
  returnColor,
  Spanning,
} from '@youshift/shared/utils';
import React, { useState, useMemo } from 'react';

import { useItrContext } from '../../../layouts/IterationRootLayout/IterationRootLayout';
import { ArrangedSectionSlot } from '../../../pages/Manager/IterationConfig/Sections/types';
import i18n from '../../../utils/i18n';
import CustomCombobox, { People } from './Combobox';
import { UserPreferenceOrEventType } from '../types';
import { borderOfSpanningSlot } from '../helpers';
import { ShowRestPeriod } from '../ShowRestPeriod';
import { getArrangedSectionSlot } from './helpers';
import { useChainContext } from '../../../layouts/ChainLayout';
import { LabelBadge } from '../../Calendars/LabelBadge';

interface SectionSlotAssignmentProps {
  slot: SectionSlot & {
    spanning: Spanning;
    start: Date;
    end: Date;
  };
  sectionColor: ColorName;
  isSlotFull: boolean;
  showRestPeriod: boolean;
  selectedPeople: Set<number>;
  isVertical?: boolean;
  week: Date[];
}

function getUserPreferenceOrEvent(
  idUser: number,
  eventsOfTheDay: SpecialEventWithoutDates[],
  preferencesOfSlot: UserPreference[],
): UserPreferenceOrEventType | null {
  const event = eventsOfTheDay?.find(event => event.id_user === idUser);
  if (event) {
    return {
      type: SpecialEventEnum.SPECIAL_EVENT,
      context: event.id_special_event_type,
    };
  }
  const preference = preferencesOfSlot?.find(pref => pref.id_user === idUser);
  if (preference) {
    return { type: preference.preference, context: preference.points };
  }
  return null;
}

export const SectionSlotAssignment = React.memo(
  ({
    slot,
    sectionColor,
    isSlotFull,
    showRestPeriod,
    selectedPeople,
    isVertical,
    week,
  }: SectionSlotAssignmentProps) => {
    const { users, epa } = useItrContext();
    const { slotLabels } = useChainContext();
    const [additionalComboboxes, setAdditionalComboboxes] = useState(0);
    const locale = localeNormalizer(i18n.language);

    const { eventsMap, preferencesMap, assignmentsMap } = epa;
    const { bySectionSlot: assignmentsBySectionSlot } = assignmentsMap;
    const { byPreferenceSlot: preferenceByPrefSlot } = preferencesMap;
    const { byDate: eventsByDate } = eventsMap;

    const slotStart = dateToString(slot.start, 'iso');
    const slotEnd = dateToString(slot.end, 'iso');

    const eventsOfTheDay = useMemo(() => {
      if (slotStart === slotEnd) return eventsByDate[slotStart] ?? [];
      return [
        ...(eventsByDate[slotStart] ?? []),
        ...(eventsByDate[slotEnd] ?? []),
      ];
    }, [slotStart, slotEnd, eventsByDate]);

    const preferencesOfSlot = useMemo(
      () => preferenceByPrefSlot[slot.id_pref_slot!] || [],
      [preferenceByPrefSlot, slot.id_pref_slot],
    );
    const assignmentsOfSlot = useMemo(
      () => assignmentsBySectionSlot[slot.id_section_slot!] || [],
      [assignmentsBySectionSlot, slot.id_section_slot],
    );

    const fadedOut = useMemo(
      () =>
        assignmentsOfSlot.length &&
        isSlotFull &&
        assignmentsOfSlot.every(
          assignment => !selectedPeople.has(assignment.id_user),
        ),
      [assignmentsOfSlot, isSlotFull, selectedPeople],
    );

    const arrangedSectionSlot = useMemo(
      () => getArrangedSectionSlot(slot, week),
      [slot, week],
    );

    const people = useMemo(
      () =>
        Object.values(users)
          .filter(
            user =>
              !assignmentsOfSlot.some(
                assignment => assignment.id_user === user.id,
              ),
          )
          .map(({ id, firstname, lastname }) => {
            const prefOrEventOrNull = getUserPreferenceOrEvent(
              id,
              eventsOfTheDay,
              preferencesOfSlot,
            );
            if (!prefOrEventOrNull) {
              return {
                id,
                name: `${firstname} ${lastname}`,
                points: 0,
                preference: UserPreferenceType.POINTS,
              };
            }
            if (prefOrEventOrNull.type === SpecialEventEnum.SPECIAL_EVENT) {
              return {
                id,
                name: `${firstname} ${lastname}`,
                id_special_event_type: prefOrEventOrNull.context,
              };
            }
            if (prefOrEventOrNull.type === UserPreferenceType.POINTS) {
              return {
                id,
                name: `${firstname} ${lastname}`,
                points: prefOrEventOrNull.context,
                preference: prefOrEventOrNull.type,
              };
            }
            return {
              id,
              name: `${firstname} ${lastname}`,
              preference: prefOrEventOrNull.type,
            };
          })
          .sort((a, b) => {
            const aHasEvent = a.preference !== UserPreferenceType.POINTS;
            const bHasEvent = b.preference !== UserPreferenceType.POINTS;
            if (aHasEvent !== bHasEvent) return aHasEvent ? 1 : -1;
            if (!aHasEvent && !bHasEvent) {
              if (a.points > 0 !== b.points > 0) return b.points - a.points;
              if ((a.points === 0) !== (b.points === 0))
                return a.points === 0 ? -1 : 1;
              return b.points - a.points;
            }
            return a.name.localeCompare(b.name);
          }),
      [users, assignmentsOfSlot, eventsOfTheDay, preferencesOfSlot],
    );

    const shade = useMemo(
      () => getShadeForLabels(slot.slot_labels),
      [slot.slot_labels],
    );
    const color = useMemo(
      () => returnColor(sectionColor, shade),
      [sectionColor, shade],
    );
    const darkestColor = useMemo(
      () => returnColor(sectionColor, 900),
      [sectionColor],
    );

    if (!slot.id_section_slot || !epa || !users) {
      return null;
    }

    return (
      <div
        key={`${arrangedSectionSlot.start_time}-${arrangedSectionSlot.day_idx}-${slot.slot_labels[0]}`}
        className={`rounded-md flex flex-col items-center justify-around relative gap-0.5 mt-1 ${fadedOut ? 'opacity-20' : ''}`}
        style={{
          backgroundColor: color,
          border: `1px solid ${darkestColor}`,
          ...(isVertical
            ? {
                gridRowStart: arrangedSectionSlot.start_position,
                gridRowEnd: arrangedSectionSlot.end_position,
              }
            : {
                gridColumnStart: arrangedSectionSlot.start_position,
                gridColumnEnd: arrangedSectionSlot.end_position,
              }),
          ...borderOfSpanningSlot(arrangedSectionSlot.spanning),
        }}
      >
        <div className="flex flex-col items-center">
          <div className="flex flex-row gap-2 justify-center w-full flex-wrap mt-1">
            {slot.slot_labels.map(labelId => (
              <LabelBadge
                key={labelId}
                labelColor={slotLabels[labelId].color}
                icon={slotLabels[labelId].icon}
              />
            ))}
          </div>
          <p
            className={`text-center md:text-xs text-xxxs ${arrangedSectionSlot.duration_minutes < 480 ? 'px-0.5 pt-1 pb-0.5' : 'px-1 pt-1 pb-0.5'}`}
          >
            {`${arrangedSectionSlot.spanning === 'end' ? '' : arrangedSectionSlot.start_time} - ${arrangedSectionSlot.end_time}`}
          </p>
        </div>
        {showRestPeriod && <ShowRestPeriod restPeriod={slot.rest_period} />}
        <div className="text-start w-full px-0.5 text-xs flex flex-col gap-1 my-1">
          {assignmentsOfSlot.map(assignment => (
            <CustomCombobox
              key={assignment.id_shift_assignment}
              people={people}
              assignment={assignment}
              assigneeName={`${users[assignment.id_user]?.firstname} ${users[assignment.id_user]?.lastname}`}
              slot={arrangedSectionSlot}
              fadedOut={!selectedPeople.has(assignment.id_user)}
            />
          ))}
          {Array.from({ length: additionalComboboxes }).map((_, idx) => (
            <CustomCombobox
              key={`extra-${idx}`}
              people={people}
              slot={arrangedSectionSlot}
              fadedOut={false}
              setAdditionalComboboxes={setAdditionalComboboxes}
              lastCombobox={idx === additionalComboboxes - 1}
            />
          ))}
          {!additionalComboboxes && (
            <button
              onClick={() => setAdditionalComboboxes(prev => prev + 1)}
              className="mx-auto"
            >
              <PlusCircleIcon className="h-4 w-4 text-gray-500" />
            </button>
          )}
        </div>
      </div>
    );
  },
);
